import React, { useCallback, useMemo } from 'react';
import { Link, useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { Button, Card, CardBody } from 'reactstrap';
import { findBreakoutIndexByName } from './breakout-utils';
import BreakoutForm from './BreakoutForm';
import styles from './Breakout.module.scss';

function emptyBreakout() {
  return {
    name: '',
    question_ids: [],
    question_set_key: '',
    question_key: '',
    grouping_mode: 'each_answer',
    groups: {},
  };
}

const Breakout = ({
  assessment,
  categoryColorMap,
  onSave,
  listPath: listPathProp,
}) => {
  const history = useHistory();
  const match = useRouteMatch();
  const { breakoutName: breakoutNameParam } = useParams();
  const isNew = match.path.endsWith('/breakouts/new');
  const routeName = isNew ? null : decodeURIComponent(breakoutNameParam || '');
  const listPath =
    listPathProp ||
    (isNew
      ? match.url.replace(/\/new$/, '')
      : match.url.replace(/\/[^/]+$/, ''));

  const existingIndex = useMemo(() => {
    if (isNew) {
      return null;
    }
    return findBreakoutIndexByName(assessment?.breakouts, routeName);
  }, [assessment?.breakouts, isNew, routeName]);

  const existing = useMemo(() => {
    if (isNew) {
      return emptyBreakout();
    }
    if (existingIndex == null) {
      return null;
    }
    const b = assessment.breakouts[existingIndex];
    return {
      id: b.id,
      name: b.name || '',
      question_ids: b.question_ids || [],
      question_set_key: b.question_set_key || '',
      question_key: b.question_key || '',
      grouping_mode: b.grouping_mode || 'each_answer',
      groups: b.groups || {},
    };
  }, [assessment, existingIndex, isNew]);

  const notFound = !isNew && existing == null;

  const handleSubmit = useCallback(
    async ({ name, question_set_key, question_key, grouping_mode, groups }) => {
      if (!assessment) {
        return;
      }
      const nextBreakout = {
        id: existing?.id,
        name,
        question_set_key,
        question_key,
        grouping_mode: grouping_mode || 'each_answer',
        groups: grouping_mode === 'custom' ? groups || {} : {},
      };
      await onSave?.(nextBreakout, { isNew });
      history.push(listPath);
    },
    [assessment, existing, history, isNew, listPath, onSave]
  );

  if (!assessment) {
    return null;
  }

  if (notFound) {
    return (
      <div className={`content survey-review-detail ${styles.root}`}>
        <Card>
          <CardBody>
            <p className="text-muted mb-3">That breakout could not be found.</p>
            <Button tag={Link} to={listPath} color="secondary">
              Back to breakouts
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className={`content survey-review-detail ${styles.root}`}>
      <BreakoutForm
        breakout={existing}
        breakouts={assessment.breakouts || []}
        excludeIndex={isNew ? null : existingIndex}
        assessment={assessment}
        categoryColorMap={categoryColorMap}
        isNew={isNew}
        listPath={listPath}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default Breakout;
